import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
    API_URL,
    GET_CANDIDATE_FORM_DETAILS_URL,
    POST_CANDIDATE_DRAFT_DATA,
    POST_NEW_REFERENCE,
    POST_NEW_GAP,
    POST_REFEREE_VERIFY_DETAILS,
    POST_CHANGE_REFEREE_EMAIL,
    POST_DELETE_REFEREE,
    POST_DECLINE_REQUEST,
    POST_NI_DOB,
    POST_SHARE_CODE,
    POST_CAN_QUESTIONS,
} from '../../../../constants/api';

import { notificationMessage } from '../../../../actions/notificationMessageActions';
import { dataURLToBlob } from '../../Shared/FormInputs/file-upload';

export const REFERENCE_TYPES = Object.freeze({
    PERSONAL: 'personal',
    PROFESSIONAL: 'professional',
    EDUCATIONAL: 'educational',
    EMPLOYMENT_GAP: 'gap',
});

export const CANDIDATE_STEPS = Object.freeze({
    WELCOME: 1,
    DASHBOARD: 2,
    REFERENCE: 3,
    REFERENCE_DETAILS_SUBMITTED: 4,
    EMPLOYMENT_GAP: 5,
    ERROR: 6,
    EDIT_REFEREE: 7,
    DELETE_REFEREE: 8,
    DECLINE: 9,
    VERIFY_ID: 10,
    NI_DOB: 11,
    SHARE_CODE: 12,
    QUESTIONS: 13,
    VERIFICATION_COMPLETE: 14,
});

export const REFERENCE_STEPS = Object.freeze({
    INTRODUCTION: 1,
    RELATIONSHIP: 2,
    REFEREE_DETAILS: 3,
    CANDIDATE_DETAILS: 4,
    SUMMARY: 5,
});

export const EMPLOYMENT_GAP_STEPS = Object.freeze({
    INTRODUCTION: 1,
    GAP_DETAILS: 2,
    UPLOAD_DOCUMENTS: 3,
    SUMMARY: 4,
});

// progress-bar label ids keyed by step id — a draft's `steps` array varies by
// reference type (personal skips CANDIDATE_DETAILS), so screens map their
// current steps through these to keep labels aligned with segments
export const REFERENCE_STEP_LABEL_IDS = Object.freeze({
    [REFERENCE_STEPS.RELATIONSHIP]: 'Forms.candidate.progress.relationship',
    [REFERENCE_STEPS.REFEREE_DETAILS]: 'Forms.candidate.progress.contact',
    [REFERENCE_STEPS.CANDIDATE_DETAILS]: 'Forms.candidate.progress.details',
    [REFERENCE_STEPS.SUMMARY]: 'Forms.candidate.progress.review',
});

export const EMPLOYMENT_GAP_STEP_LABEL_IDS = Object.freeze({
    [EMPLOYMENT_GAP_STEPS.GAP_DETAILS]: 'Forms.candidate.progress.details',
    [EMPLOYMENT_GAP_STEPS.UPLOAD_DOCUMENTS]: 'Forms.candidate.progress.evidence',
    [EMPLOYMENT_GAP_STEPS.SUMMARY]: 'Forms.candidate.progress.review',
});

const initialSteps = [
    REFERENCE_STEPS.RELATIONSHIP,
    REFERENCE_STEPS.REFEREE_DETAILS,
    REFERENCE_STEPS.CANDIDATE_DETAILS,
    REFERENCE_STEPS.SUMMARY,
];

const initialState = {
    loading: false,
    saving: false,
    submitting: false,
    token: '',
    error: {},
    details: {},
    // set from the ?iscompany=1 URL param — an employer/agency is filling in the
    // referee details on the candidate's behalf, which flips the form to a
    // third-person perspective and skips the candidate identity-verification step
    isCompany: 0,
    filesUploaded: [],
    editingRefereeIndex: null,
    draft_data: {
        currentStep: 1,
        currentReference: 0,
        references: [],
    },
};

const initialDraftReference = {
    currentProgressStep: 1,
    steps: initialSteps,
    history: [1],
    type: '',
    referee_fullname: '',
    referee_email: '',
    referee_phone: '',
    referee_relationship: '',
    referee_company: '',
    referee_known_since: '',
    job_title: '',
    school: '',
    course_name: '',
    start_date: '',
    end_date: '',
    gap_upload_id: '',
};

// build a YYYY-MM-DD string from separate day/month/year values, or '' when
// any part is missing or the combination isn't a real date
const buildPrefillDate = (day, month, year) => {
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    if (!dayNum || !monthNum || !yearNum) {
        return '';
    }

    const date = new Date(yearNum, monthNum - 1, dayNum);

    if (
        date.getFullYear() !== yearNum ||
        date.getMonth() !== monthNum - 1 ||
        date.getDate() !== dayNum
    ) {
        return '';
    }

    return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
};

// legacy add_referee_defaults: prefill a new reference draft from URL params
// (relationship, name, email, telephone, company, jobtitle, startday/-month/-year,
// endday/-month/-year). Bad values are silently ignored.
const getReferencePrefillFromUrl = (details) => {
    const prefill = {};

    try {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('name')) prefill.referee_fullname = urlParams.get('name');
        if (urlParams.get('email')) prefill.referee_email = urlParams.get('email');
        if (urlParams.get('telephone')) prefill.phoneNumber = urlParams.get('telephone');
        if (urlParams.get('company')) prefill.referee_company = urlParams.get('company');
        if (urlParams.get('jobtitle')) prefill.job_title = urlParams.get('jobtitle');

        const relationshipId = parseInt(urlParams.get('relationship'), 10);
        const hiddenTypeIds = (details?.referee_types_hidden || '')
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));
        const relationshipType =
            !isNaN(relationshipId) && !hiddenTypeIds.includes(relationshipId)
                ? (details?.relationship_types || []).find(
                      (relationship) => relationship.id === relationshipId
                  )
                : null;

        if (relationshipType) {
            prefill.referee_relationship = relationshipType.id;
        }

        const startDate = buildPrefillDate(
            urlParams.get('startday'),
            urlParams.get('startmonth'),
            urlParams.get('startyear')
        );
        const endDate = buildPrefillDate(
            urlParams.get('endday'),
            urlParams.get('endmonth'),
            urlParams.get('endyear')
        );

        if (relationshipType?.type === REFERENCE_TYPES.PERSONAL) {
            if (startDate) prefill.referee_known_since = startDate;
        } else {
            if (startDate) prefill.start_date = startDate;
            if (endDate) prefill.end_date = endDate;
        }
    } catch (error) {
        // malformed URL values must never break the form
        console.error('Error reading reference prefill from URL:', error);
        return {};
    }

    return prefill;
};

// prefill only applies while nothing has been submitted or started yet
const canPrefillFromUrl = (details, references) =>
    !(details?.refereesadded > 0) &&
    !(references || []).some((reference) => reference.type || reference.referee_fullname);

const candidateOnboardingSlice = createSlice({
    name: 'candidate',
    initialState,
    reducers: {
        setCurrentStep: (state, action) => {
            state.draft_data.currentStep = action.payload;
            // don't leak stale errors from a previous screen into the next one
            state.error = {};
        },
        setSteps: (state, action) => {
            if (action.payload === REFERENCE_TYPES.PERSONAL) {
                state.draft_data.references[state.draft_data.currentReference].steps = [
                    REFERENCE_STEPS.RELATIONSHIP,
                    REFERENCE_STEPS.REFEREE_DETAILS,
                    REFERENCE_STEPS.SUMMARY,
                ];
            } else if (action.payload === REFERENCE_TYPES.EMPLOYMENT_GAP) {
                state.draft_data.references[state.draft_data.currentReference].steps = [
                    EMPLOYMENT_GAP_STEPS.GAP_DETAILS,
                    EMPLOYMENT_GAP_STEPS.UPLOAD_DOCUMENTS,
                    EMPLOYMENT_GAP_STEPS.SUMMARY,
                ];
            } else {
                state.draft_data.references[state.draft_data.currentReference].steps = initialSteps;
            }
        },
        previousReferenceStep: (state) => {
            const currentReference = state.draft_data.currentReference;

            if (state.draft_data.references[currentReference].history.length >= 1) {
                const previousStep =
                    state.draft_data.references[currentReference].history.length - 1;

                state.draft_data.references[currentReference].currentProgressStep =
                    state.draft_data.references[currentReference].history[previousStep];
                state.draft_data.references[currentReference].history.pop();
            }
        },
        nextReferenceStep: (state, action) => {
            const currentReference = state.draft_data.currentReference;
            const nextStep =
                action.payload ||
                state.draft_data.references[currentReference].currentProgressStep + 1;

            state.draft_data.references[currentReference].history.push(
                state.draft_data.references[currentReference].currentProgressStep
            );
            state.draft_data.references[currentReference].currentProgressStep = nextStep;
        },
        goToReferenceStep: (state, action) => {
            const currentReference = state.draft_data.currentReference;

            state.draft_data.references[currentReference].history.push(
                state.draft_data.references[currentReference].currentProgressStep
            );

            state.draft_data.references[currentReference].currentProgressStep = action.payload;
        },
        setCandidateToken: (state, action) => {
            state.token = action.payload;
        },
        setIsCompany: (state, action) => {
            state.isCompany = action.payload;
        },
        createDraftReference: (state, action) => {
            const isGap = action?.payload?.type === REFERENCE_TYPES.EMPLOYMENT_GAP;

            const prefill =
                !isGap && canPrefillFromUrl(state.details, state.draft_data.references)
                    ? getReferencePrefillFromUrl(state.details)
                    : {};

            state.draft_data.references.push({
                ...initialDraftReference,
                ...prefill,
                type: action?.payload?.type || '',
            });
            state.draft_data.currentReference = state.draft_data.references.length - 1;
        },
        updateActiveReference: (state, action) => {
            state.draft_data.currentReference = action.payload;
        },
        saveDraftReference: (state, action) => {
            if (action.payload.type) {
                state.draft_data.references[state.draft_data.currentReference].type =
                    action.payload.type;
            }

            state.draft_data.references[state.draft_data.currentReference] = {
                ...state.draft_data.references[state.draft_data.currentReference],
                ...action.payload.data,
            };
        },
        setDraftReferences: (state, action) => {
            state.draft_data.references = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        saveFileUpload: (state, action) => {
            // each gap draft collects a single evidence file, keyed by the
            // gap_upload_id stored on the draft — keep files still referenced by
            // other in-progress drafts, drop superseded ones
            const referencedIds = state.draft_data.references
                .map((reference) => reference.gap_upload_id)
                .filter(Boolean);

            state.filesUploaded = [
                ...state.filesUploaded.filter((file) =>
                    referencedIds.includes(file.upload_id)
                ),
                action.payload,
            ];
        },
        setEditingRefereeIndex: (state, action) => {
            state.editingRefereeIndex = action.payload;
        },
        setNiDobPrompted: (state) => {
            state.draft_data.niDobPrompted = true;
        },
        setNiDobOrigin: (state, action) => {
            state.niDobOrigin = action.payload;
        },
        setQuestionsCompleted: (state) => {
            state.draft_data.questionsCompleted = true;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCandidateDetails.pending, (state) => {
            const isInitialLoad = !state.details || Object.keys(state.details).length === 0;
            if (isInitialLoad) {
                state.loading = true;
            }
        });
        builder.addCase(getCandidateDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || { errorType: 'unknown', message: 'An error occurred' };
            state.draft_data.currentStep = CANDIDATE_STEPS.ERROR;
        });
        builder.addCase(getCandidateDetails.fulfilled, (state, action) => {
            const { draft_data, employmentGapsAllowed, ...details } = action.payload;

            state.loading = false;
            state.details = details;
            state.employmentGapsAllowed = employmentGapsAllowed;
            state.draft_data = {
                ...state.draft_data,
                ...draft_data,
            };
        });
        builder.addCase(saveCandidateDraftData.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveCandidateDraftData.rejected, (state) => {
            state.saving = false;
        });
        builder.addCase(saveCandidateDraftData.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(verifyContactDetails.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(verifyContactDetails.rejected, (state) => {
            state.saving = false;
        });
        builder.addCase(verifyContactDetails.fulfilled, (state) => {
            state.saving = false;
        });
        builder.addCase(submitReference.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(submitReference.rejected, (state) => {
            state.submitting = false;
        });
        builder.addCase(submitReference.fulfilled, (state) => {
            state.submitting = false;
        });
        builder.addCase(submitGap.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(submitGap.rejected, (state) => {
            state.submitting = false;
        });
        builder.addCase(submitGap.fulfilled, (state, action) => {
            state.submitting = false;
            // keep the evidence file around when the server rejected the gap so
            // the user can fix the errors and resubmit; on success only drop the
            // submitted draft's file — other gap drafts keep theirs
            if (action.payload?.success) {
                state.filesUploaded = state.filesUploaded.filter(
                    (file) => file.upload_id !== action.payload.gapUploadId
                );
            }
        });
        builder.addCase(changeRefereeEmail.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(changeRefereeEmail.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(changeRefereeEmail.fulfilled, (state) => {
            state.submitting = false;
            state.editingRefereeIndex = null;
        });
        builder.addCase(deleteReferee.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(deleteReferee.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(deleteReferee.fulfilled, (state) => {
            state.submitting = false;
            state.editingRefereeIndex = null;
        });
        builder.addCase(declineRequest.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(declineRequest.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(declineRequest.fulfilled, (state) => {
            state.submitting = false;
        });
        builder.addCase(submitNiDob.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(submitNiDob.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(submitNiDob.fulfilled, (state) => {
            state.submitting = false;
            state.draft_data.niDobProvided = true;
        });
        builder.addCase(submitShareCode.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(submitShareCode.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(submitShareCode.fulfilled, (state) => {
            state.submitting = false;
        });
        builder.addCase(submitCanQuestions.pending, (state) => {
            state.submitting = true;
            state.error = {};
        });
        builder.addCase(submitCanQuestions.rejected, (state, action) => {
            state.submitting = false;
            state.error = { inputs: action.payload };
        });
        builder.addCase(submitCanQuestions.fulfilled, (state) => {
            state.submitting = false;
        });
    },
});

// get candidate details from the basic endpoint
export const getCandidateDetails = createAsyncThunk(
    'candidate/getCandidateDetails',
    async (data, thunkAPI) => {
        const { token, overwriteCurrentStep = 0 } = data;

        try {
            const url = `${API_URL}${GET_CANDIDATE_FORM_DETAILS_URL}`;
            const config = {
                params: {
                    token: token || thunkAPI.getState().forms.candidate.token,
                },
                withCredentials: true,
            };

            const response = await axios.get(url, config);

            if (response?.data?.status === 'error' || !response?.data?.data?.[0]) {
                return thunkAPI.rejectWithValue({
                    errorType: 'not_found',
                    message: response?.data?.message || 'Request not found',
                });
            }

            const responseData = response.data.data[0];

            if (responseData.declinestatus > 0) {
                return thunkAPI.rejectWithValue({
                    errorType: 'declined',
                    message: 'This request has been declined',
                });
            }

            const draft_data = responseData?.draft_data || initialState.draft_data;

            // keep showing the welcome screen (with the employer's message) until
            // the candidate has actually started something
            const hasStarted =
                responseData.refereesadded > 0 ||
                draft_data.niDobProvided ||
                draft_data.questionsCompleted ||
                draft_data.references?.some(
                    (reference) => reference.type || reference.referee_fullname
                );
            let currentStep = hasStarted ? CANDIDATE_STEPS.DASHBOARD : CANDIDATE_STEPS.WELCOME;

            // RTW-only checks skip references entirely
            if (responseData.is_rtw_check === 1) {
                const notVerified = responseData.yverified === '0000-00-00 00:00:00';
                const hasQuestions = responseData.can_questions?.length > 0;

                currentStep = notVerified
                    ? hasQuestions
                        ? CANDIDATE_STEPS.QUESTIONS
                        : CANDIDATE_STEPS.VERIFY_ID
                    : CANDIDATE_STEPS.VERIFICATION_COMPLETE;
            }

            if (overwriteCurrentStep) {
                currentStep = overwriteCurrentStep;
            }

            const { refereesadded, refnum, required_range_months } = responseData;
            const draftReferences = draft_data?.references || [];
            const placeholderRefs = Math.max(0, refnum - refereesadded - draftReferences.length);
            // legacy add_referee_defaults: seed the first reference from URL params
            const prefill = canPrefillFromUrl(responseData, draftReferences)
                ? getReferencePrefillFromUrl(responseData)
                : {};
            const placeholderReferences = Array.from({ length: placeholderRefs }).map(
                (_, index) => ({
                    ...initialDraftReference,
                    ...(index === 0 && draftReferences.length === 0 ? prefill : {}),
                })
            );
            const employmentGapsAllowed = required_range_months > 0;

            return {
                ...responseData,
                employmentGapsAllowed,
                draft_data: {
                    ...draft_data,
                    currentStep,
                    references: [...draftReferences, ...placeholderReferences],
                },
            };
        } catch (error) {
            console.error('Error getting candidate details:', error);
            return thunkAPI.rejectWithValue({
                errorType: 'network',
                message: 'Unable to load your request. Please try again later.',
            });
        }
    }
);

// save the entire candidate draft data
export const saveCandidateDraftData = createAsyncThunk(
    'candidate/saveCandidateDraftData',
    async (draftData, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_CANDIDATE_DRAFT_DATA}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append(
                'inputdraftdata',
                JSON.stringify(draftData || thunkAPI.getState().forms.candidate.draft_data)
            );

            const config = {
                withCredentials: true,
            };

            const response = await axios.post(url, params, config);

            return response?.data?.data || {};
        } catch (error) {
            console.error('Error posting candidate draft data:', error);
            return thunkAPI.rejectWithValue({ error: 'network' });
        }
    }
);

// on success proceed to the next step
export const saveDraftReferenceData = createAsyncThunk(
    'candidate/saveDraftReferenceData',
    async ({ type = '', data, nextStep }, thunkAPI) => {
        thunkAPI.dispatch(setError({}));
        thunkAPI.dispatch(saveDraftReference({ data, type }));
        const draftDataAction = await thunkAPI.dispatch(saveCandidateDraftData());

        if (draftDataAction.meta.requestStatus !== 'fulfilled') {
            thunkAPI.dispatch(setError({ network: true }));
            return thunkAPI.rejectWithValue({ error: 'network' });
        }

        thunkAPI.dispatch(nextReferenceStep(nextStep));
    }
);

// same as the action above but without proceeding to the next step
export const updateDraftReferenceDetails = createAsyncThunk(
    'candidate/updateDraftReferenceDetails',
    async ({ data }, thunkAPI) => {
        thunkAPI.dispatch(setError({}));
        thunkAPI.dispatch(saveDraftReference({ data }));
        const draftDataAction = await thunkAPI.dispatch(saveCandidateDraftData());

        if (draftDataAction.meta.requestStatus !== 'fulfilled') {
            thunkAPI.dispatch(setError({ network: true }));
            return thunkAPI.rejectWithValue({ error: 'network' });
        }
    }
);

// handle date input, especially present dates
const handleDate = (dateInput) => {
    if (!dateInput || dateInput === '0000-00-00') {
        return {
            getDate: () => '0',
            getMonth: () => '0',
            getFullYear: () => '0000',
        };
    } else {
        const date = new Date(dateInput);
        return {
            getDate: () => date.getDate(),
            getMonth: () => parseInt(date.getMonth() + 1),
            getFullYear: () => date.getFullYear(),
        };
    }
};

// utility function to append params to the form data or not if the value is empty
const checkAndAppendParam = (params, key, value) => {
    if (value) {
        params.append(key, value);
    }
};

// submit the reference form
export const submitReference = createAsyncThunk(
    'candidate/submitReference',
    async (data, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_NEW_REFERENCE}`;
            const params = new URLSearchParams();
            const draftData = thunkAPI.getState().forms.candidate.draft_data;
            const { currentReference, references } = draftData;
            const reference = references[currentReference];
            const startDate =
                reference.type === REFERENCE_TYPES.PERSONAL
                    ? handleDate(reference.referee_known_since)
                    : handleDate(reference.start_date);
            const endDate = handleDate(reference.end_date);

            //other params
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputemail_validation_off', reference.email_validation_off ? 1 : 0);
            params.append('referer', location.href);
            params.append('inputrelationship', reference.referee_relationship);
            params.append('inputphone', reference.phoneNumber);
            params.append('inputphonetype', reference.phoneNumberType);

            checkAndAppendParam(params, 'inputfullname', reference.referee_fullname);
            checkAndAppendParam(params, 'inputcompany', reference.referee_company);
            checkAndAppendParam(params, 'inputemail', reference.referee_email);
            checkAndAppendParam(params, 'inputschool', reference.school);
            checkAndAppendParam(params, 'inputcourse', reference.course_name);
            checkAndAppendParam(params, 'inputjobtitle', reference.job_title);
            checkAndAppendParam(params, 'inputstartday', startDate.getDate());
            checkAndAppendParam(params, 'inputstartmonth', startDate.getMonth());
            checkAndAppendParam(params, 'inputstartyear', startDate.getFullYear());
            checkAndAppendParam(params, 'inputendday', endDate.getDate());
            checkAndAppendParam(params, 'inputendmonth', endDate.getMonth());
            checkAndAppendParam(params, 'inputendyear', endDate.getFullYear());

            const config = {
                withCredentials: true,
            };

            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                thunkAPI.dispatch(
                    notificationMessage({
                        message:
                            'An error occurred. Please scroll to the bottom of the page to see the errors',
                        type: 'error',
                    })
                );

                return thunkAPI.dispatch(
                    setError({
                        inputs: response.data.data,
                    })
                );
            }

            if (response.data.status === 'success') {
                // Delete the reference from the saved draft data
                const updatedReferences = references.filter(
                    (_, index) => index !== currentReference
                );

                const updatedDraftData = {
                    ...draftData,
                    references: updatedReferences,
                };

                // only advance to the submitted screen once the refetch has
                // resolved, so it never renders with stale counts
                await thunkAPI.dispatch(saveCandidateDraftData(updatedDraftData));
                await thunkAPI.dispatch(
                    getCandidateDetails({
                        overwriteCurrentStep: CANDIDATE_STEPS.REFERENCE_DETAILS_SUBMITTED,
                    })
                );
            }

            return { success: response.data.status === 'success', data: response.data.data };
        } catch (error) {
            console.error('Error submitting reference:', error);
            thunkAPI.dispatch(
                notificationMessage({
                    message: 'An error occurred. Please try again later.',
                    type: 'error',
                })
            );
            thunkAPI.dispatch(setError({ network: true }));
            return thunkAPI.rejectWithValue({ error: 'network' });
        }
    }
);

// submit an employment gap
export const submitGap = createAsyncThunk('candidate/submitGap', async (data, thunkAPI) => {
    try {
        const url = `${API_URL}${POST_NEW_GAP}`;
        const params = new FormData();
        const draftData = thunkAPI.getState().forms.candidate.draft_data;
        const { currentReference, references } = draftData;
        const reference = references[currentReference];
        const startDate = handleDate(reference.gap_start_date);
        const endDate = reference.gap_currently_present
            ? { getDate: () => '0', getMonth: () => '0', getFullYear: () => '0000' }
            : handleDate(reference.gap_end_date);

        params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
        params.append('inputrelationship', reference.gap_reason);
        params.append('inputgapexplain', reference.gap_explanation || '');
        params.append('inputstartday', startDate.getDate());
        params.append('inputstartmonth', startDate.getMonth());
        params.append('inputstartyear', startDate.getFullYear());
        params.append('inputendday', endDate.getDate());
        params.append('inputendmonth', reference.gap_currently_present ? 0 : endDate.getMonth());
        params.append('inputendyear', endDate.getFullYear());
        params.append('referer', location.href);

        const { filesUploaded } = thunkAPI.getState().forms.candidate;
        // only this draft's evidence file — other in-progress gap drafts keep
        // their own uploads in the same array
        const draftFiles = filesUploaded.filter(
            (file) => file?.upload_id === reference.gap_upload_id
        );
        const usableFiles = draftFiles.filter((file) => file?.file || file?.preview);

        // a gap_upload_id without a usable local file means the original File was
        // lost (e.g. after a refresh) - block the submit and ask for a re-upload
        if (
            usableFiles.length < draftFiles.length ||
            (reference.gap_upload_id && usableFiles.length === 0)
        ) {
            thunkAPI.dispatch(setError({ fileMissing: true }));
            return thunkAPI.rejectWithValue({ error: 'file_missing' });
        }

        usableFiles.forEach((file) => {
            if (file.file) {
                // the raw File selected by the user
                params.append('inputgapupload', file.file);
            } else {
                // restored draft: rebuild the upload from its base64 preview
                params.append('inputgapupload', dataURLToBlob(file.preview), file.name);
            }
        });

        const config = {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
        };

        const response = await axios.post(url, params, config);

        if (response.data.status === 'error') {
            thunkAPI.dispatch(
                notificationMessage({
                    message:
                        'An error occurred. Please scroll to the bottom of the page to see the errors',
                    type: 'error',
                })
            );
            return thunkAPI.dispatch(setError({ inputs: response.data.data }));
        }

        if (response.data.status === 'success') {
            const updatedReferences = references.filter((_, index) => index !== currentReference);
            const updatedDraftData = { ...draftData, references: updatedReferences };

            // only advance to the submitted screen once the refetch has resolved,
            // so it never renders with stale counts
            await thunkAPI.dispatch(saveCandidateDraftData(updatedDraftData));
            await thunkAPI.dispatch(
                getCandidateDetails({
                    overwriteCurrentStep: CANDIDATE_STEPS.REFERENCE_DETAILS_SUBMITTED,
                })
            );
        }

        return {
            success: response.data.status === 'success',
            data: response.data.data,
            gapUploadId: reference.gap_upload_id,
        };
    } catch (error) {
        console.error('Error submitting gap:', error);
        thunkAPI.dispatch(
            notificationMessage({ message: 'An error occurred. Please try again later.', type: 'error' })
        );
        thunkAPI.dispatch(setError({ network: true }));
        return thunkAPI.rejectWithValue({ error: 'network' });
    }
});

// change a submitted referee's email address
export const changeRefereeEmail = createAsyncThunk(
    'candidate/changeRefereeEmail',
    async ({ rfid, email }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_CHANGE_REFEREE_EMAIL}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputrfid', rfid);
            params.append('inputemail', email);
            params.append('referer', location.href);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            if (response.data.status === 'success') {
                await thunkAPI.dispatch(getCandidateDetails({ overwriteCurrentStep: CANDIDATE_STEPS.DASHBOARD }));
            }

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Error changing referee email:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// delete a submitted referee
export const deleteReferee = createAsyncThunk(
    'candidate/deleteReferee',
    async ({ rfid, reason }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_DELETE_REFEREE}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputrfid', rfid);
            params.append('inputreason', reason);
            params.append('referer', location.href);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            if (response.data.status === 'success') {
                await thunkAPI.dispatch(getCandidateDetails({ overwriteCurrentStep: CANDIDATE_STEPS.DASHBOARD }));
            }

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Error deleting referee:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// decline the entire request
export const declineRequest = createAsyncThunk(
    'candidate/declineRequest',
    async ({ reason }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_DECLINE_REQUEST}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputdeclinereason', reason);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            if (response.data.status === 'success') {
                // setCurrentStep clears errors, so set the declined state afterwards
                thunkAPI.dispatch(setCurrentStep(CANDIDATE_STEPS.ERROR));
                thunkAPI.dispatch(
                    setError({ errorType: 'declined', message: 'This request has been declined' })
                );
            }

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Error declining request:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// submit the candidate's NI/Tax ID and date of birth (optional, speeds up references)
export const submitNiDob = createAsyncThunk(
    'candidate/submitNiDob',
    async ({ niNumber = '', dob = '' }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_NI_DOB}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputninumber', niNumber);
            params.append('inputdob', dob);
            params.append('referer', location.href);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            return { success: true };
        } catch (error) {
            console.error('Error submitting NI/DOB:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// submit a UK Government Share Code to prove right to work
export const submitShareCode = createAsyncThunk(
    'candidate/submitShareCode',
    async ({ shareCode, dob }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_SHARE_CODE}`;
            const params = new URLSearchParams();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);
            params.append('inputsharecode', shareCode);
            params.append('inputdob', dob);
            params.append('referer', location.href);

            const config = { withCredentials: true };
            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            if (response.data.status === 'success') {
                await thunkAPI.dispatch(
                    getCandidateDetails({
                        overwriteCurrentStep: CANDIDATE_STEPS.VERIFICATION_COMPLETE,
                    })
                );
            }

            return { success: true };
        } catch (error) {
            console.error('Error submitting share code:', error);
            return thunkAPI.rejectWithValue({ error: 'An error occurred. Please try again.' });
        }
    }
);

// submit the candidate's self-assessment question answers
export const submitCanQuestions = createAsyncThunk(
    'candidate/submitCanQuestions',
    async ({ answers }, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_CAN_QUESTIONS}`;
            const params = new FormData();
            params.append('inputtoken', thunkAPI.getState().forms.candidate.token);

            Object.entries(answers).forEach(([rqid, value]) => {
                if (value instanceof File) {
                    params.append(`inputquestion[${rqid}]`, value, value.name);
                } else {
                    params.append(`inputquestion[${rqid}]`, value ?? '');
                }
            });

            const config = {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            };

            const response = await axios.post(url, params, config);

            if (response.data.status === 'error') {
                return thunkAPI.rejectWithValue(response.data.data);
            }

            if (response.data.status === 'success') {
                const { details, isCompany } = thunkAPI.getState().forms.candidate;
                // when a company fills the form on the candidate's behalf the
                // candidate isn't present to verify their identity, so skip it
                // (mirrors the legacy form's afterAddRefereeStep iscompany gate)
                const needsVerification =
                    isCompany != 1 &&
                    (details.is_rtw_check === 1 ||
                        (details.skip_verify_can === 0 &&
                            details.yrequired === 1 &&
                            details.yverified === '0000-00-00 00:00:00'));

                thunkAPI.dispatch(setQuestionsCompleted());
                await thunkAPI.dispatch(saveCandidateDraftData());
                thunkAPI.dispatch(
                    setCurrentStep(
                        needsVerification
                            ? CANDIDATE_STEPS.VERIFY_ID
                            : CANDIDATE_STEPS.DASHBOARD
                    )
                );
            }

            return { success: true };
        } catch (error) {
            console.error('Error submitting questions:', error);
            // sentinel rendered as a translated alert by the Questions screen
            return thunkAPI.rejectWithValue({ network: true });
        }
    }
);

// verify the contact details of the referee i.e. is the email valid and are personal email addresses allowed?
export const verifyContactDetails = createAsyncThunk(
    'candidate/verifyContactDetails',
    async (data, thunkAPI) => {
        try {
            const url = `${API_URL}${POST_REFEREE_VERIFY_DETAILS}`;
            const params = new URLSearchParams();

            const { token, draft_data } = thunkAPI.getState().forms.candidate;
            const { currentReference, references } = draft_data;
            const { referee_relationship } = references[currentReference];
            const { email = '', validationOff = 0 } = data;

            params.append('inputtoken', token);
            params.append('inputrelationship', referee_relationship);
            params.append('inputemail', email);
            params.append('inputemail_validation_off', validationOff ? 1 : 0);

            const config = {
                withCredentials: true,
            };

            const response = await axios.post(url, params, config);

            return response?.data?.data || {};
        } catch (error) {
            console.error('Error verifying contact details:', error);
            return thunkAPI.rejectWithValue({ error: 'network' });
        }
    }
);

export const {
    setCurrentStep,
    setCandidateToken,
    setIsCompany,
    createDraftReference,
    updateActiveReference,
    saveDraftReference,
    previousReferenceStep,
    nextReferenceStep,
    goToReferenceStep,
    setSteps,
    setError,
    setDraftReferences,
    saveFileUpload,
    setEditingRefereeIndex,
    setNiDobPrompted,
    setNiDobOrigin,
    setQuestionsCompleted,
} = candidateOnboardingSlice.actions;

export default candidateOnboardingSlice.reducer;
